import { useEffect } from 'react';
import { ChevronsUp, X, ShieldCheck, Banknote, Hourglass, Trophy } from 'lucide-react';
import { ModalRoot } from '@/components/ui/ModalRoot';
import { Z_MODALS } from '@/lib/zIndex';
import { getLevelUpCost, getRadiusKm, isAtCap, LEVEL_MAX } from '@/data/leveling';
import { formatEuro } from '@/lib/format';

interface LevelUpDialogProps {
  open: boolean;
  level: number;
  credits: number;
  isSandbox: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * Phase 8.6.2 — LevelUpDialog redesign.
 *  - SCHUTZ AKTIV badge (current vs. next radius)
 *  - Side-by-side comparison
 *  - Kosten / Dauer / Aktion-Boxen
 *  - Confirm button uses ShieldCheck icon
 */
export function LevelUpDialog({
  open,
  level,
  credits,
  isSandbox,
  onClose,
  onConfirm,
}: LevelUpDialogProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const atCap = isAtCap(level);
  const cost = atCap ? 0 : getLevelUpCost(level);
  const currentRadius = getRadiusKm(level);
  const nextRadius = atCap ? currentRadius : getRadiusKm(level + 1);
  const affordable = isSandbox || credits >= cost;
  const radiusDelta = nextRadius - currentRadius;

  return (
    <ModalRoot
      onClose={onClose}
      zIndex={Z_MODALS}
      contentClass="w-full max-w-lg rounded-lg border border-border-strong bg-bg-secondary"
    >
      {/* Hero */}
      <header className="relative overflow-hidden bg-gradient-to-br from-accent-dispatch to-blue-700 px-6 py-5 text-white">
        <div className="flex items-start gap-3">
          <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-white/20">
            {atCap ? (
              <Trophy className="h-6 w-6" aria-hidden />
            ) : (
              <ChevronsUp className="h-6 w-6" aria-hidden />
            )}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-widest opacity-80">
              Stadtebene · Einsatzgebiet
            </p>
            <h3 className="font-display text-2xl font-bold">
              {atCap ? `Level ${LEVEL_MAX} erreicht` : `Level ${level} → ${level + 1}`}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-white/15 transition hover:bg-white/25"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      </header>

      <div className="space-y-4 p-6">
        {atCap ? (
          <p className="text-sm leading-relaxed text-fg-secondary">
            Du hast das maximale Level {LEVEL_MAX} erreicht. Dein Einsatzgebiet umfasst dauerhaft{' '}
            <span className="font-semibold text-fg-primary">{currentRadius} km</span> rund um die
            Stadtmitte.
          </p>
        ) : (
          <>
            {/* Side-by-side comparison */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-border-subtle bg-bg-elevated/40 p-4">
                <p className="text-[10px] uppercase tracking-wider text-fg-muted">Aktuell</p>
                <p className="mt-1 font-display text-2xl font-bold tabular-nums text-fg-primary">
                  {currentRadius} km
                </p>
                <p className="mt-1 text-[10px] text-fg-muted">
                  Level {level}
                </p>
              </div>
              <div className="rounded-lg border border-accent-dispatch/40 bg-accent-dispatch/10 p-4">
                <p className="text-[10px] uppercase tracking-wider text-accent-dispatch">Neu</p>
                <p className="mt-1 font-display text-2xl font-bold tabular-nums text-accent-dispatch">
                  {nextRadius} km
                </p>
                <p className="mt-1 text-[10px] text-accent-dispatch">
                  Level {level + 1} · +{radiusDelta.toFixed(radiusDelta < 1 ? 1 : 0)} km
                </p>
              </div>
            </div>

            {/* SCHUTZ AKTIV badge */}
            <div className="flex items-center gap-2 rounded-md border border-status-available/40 bg-status-available/10 px-3 py-2">
              <ShieldCheck className="h-4 w-4 flex-shrink-0 text-status-available" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-bold uppercase tracking-widest text-status-available">
                  Schutz aktiv
                </p>
                <p className="text-[11px] text-fg-secondary">
                  Bestehende Wachen bleiben bestehen, neue Spawn-Zonen werden freigeschaltet.
                </p>
              </div>
            </div>

            {/* Kosten / Dauer boxes */}
            <div className="grid grid-cols-2 gap-3">
              <InfoBox
                icon={Banknote}
                label="Kosten"
                value={isSandbox ? 'Sandbox · frei' : formatEuro(cost)}
                tone={!affordable ? 'warn' : 'default'}
              />
              <InfoBox
                icon={Hourglass}
                label="Dauer"
                value="sofort"
              />
            </div>

            <div className="rounded-md border border-border-subtle bg-bg-elevated/40 p-3">
              <div className="flex items-center justify-between text-xs">
                <span className="text-fg-muted">Dein Guthaben</span>
                <span
                  className={[
                    'font-display text-base font-bold tabular-nums',
                    affordable ? 'text-fg-primary' : 'text-accent-emergency',
                  ].join(' ')}
                >
                  {formatEuro(credits)}
                </span>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-border-strong bg-bg-elevated px-4 py-2 text-sm font-semibold text-fg-primary transition hover:bg-bg-elevated/70"
              >
                Abbrechen
              </button>
              <button
                type="button"
                disabled={!affordable}
                onClick={onConfirm}
                autoFocus
                className={[
                  'flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-semibold text-white transition',
                  affordable
                    ? 'bg-accent-dispatch hover:bg-accent-dispatch-hover'
                    : 'cursor-not-allowed bg-bg-elevated text-fg-muted',
                ].join(' ')}
              >
                <ShieldCheck className="h-4 w-4" aria-hidden />
                {affordable ? 'Level kaufen' : 'Credits fehlen'}
              </button>
            </div>
          </>
        )}
      </div>
    </ModalRoot>
  );
}

function InfoBox({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: typeof Banknote;
  label: string;
  value: string;
  tone?: 'warn' | 'default';
}) {
  const valueClass =
    tone === 'warn' ? 'text-accent-emergency' : 'text-fg-primary';
  return (
    <div className="rounded-md border border-border-subtle bg-bg-elevated/40 p-3">
      <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-fg-muted">
        <Icon className="h-3 w-3" aria-hidden />
        {label}
      </p>
      <p className={['mt-1 font-display text-base font-bold tabular-nums', valueClass].join(' ')}>
        {value}
      </p>
    </div>
  );
}
